import moment from 'moment'
import { cloneDeep, flow, isEqual, orderBy, set as setProperty, size, sortBy } from 'lodash'

import { getDb, mapTaskData, mapGoalData, mapMilestoneData, globalWatcherUnsub } from './firestore'
import { createCachedSnapshotGate } from './cachedSnapshotGate'
import { getRoleIdsVisibleToField } from './firestoreAccess'
import {
    confirmOptimisticTaskForSubscriber,
    OPTIMISTIC_TASK_REMOVED,
    OPTIMISTIC_TASK_SETTLED,
    subscribeToOptimisticTaskCreates,
} from './Tasks/optimisticTaskCreate'
import store from '../../redux/store'
import {
    setGlobalDataByProject,
    setOpenSubtasksMap,
    setOpenTasksMap,
    setTaskListWatchersVars,
    updateSubtaskByTask,
    setLaterTasksExpanded,
    setTaskListSingleLoading,
    updateFilteredOpenTasks,
    updateThereAreHiddenNotMainTasks,
    updateInitialLoadingEndOpenTasks,
    updateInitialLoadingEndObservedTasks,
    setTodayEmptyGoalsTotalAmountInOpenTasksView,
    updateThereAreNotTasksInFirstDay,
    updateOpenTasks,
    setOpenMilestonesInProjectInTasks,
    setDoneMilestonesInProjectInTasks,
    setGoalsInProjectInTasks,
    setSomedayTasksExpanded,
} from '../../redux/actions'
import { FEED_PUBLIC_FOR_ALL } from '../../components/Feeds/Utils/FeedsConstants'
import { BACKLOG_DATE_NUMERIC, BACKLOG_DATE_STRING } from '../../components/TaskListView/Utils/TasksHelper'
import { DEFAULT_WORKSTREAM_ID, WORKSTREAM_ID_PREFIX } from '../../components/Workstreams/WorkstreamHelper'
import { BACKLOG_MILESTONE_ID, DYNAMIC_PERCENT, getOwnerId } from '../../components/GoalsView/GoalsHelper'
import { ESTIMATION_0_MIN, getEstimationRealValue } from '../EstimationHelper'
import { filterOpenTasks } from '../../components/HashtagFilters/FilterHelpers/FilterTasks'
import {
    filterOpenTasksSectionsByPriority,
    filterOpenTasksSectionsByVmState,
} from '../../components/TaskListView/PriorityFilters/taskPriorityFilterHelper'
import { sortTasksByPriority } from '../TaskPriority'
import { buildWorkflowTaskGroups } from './workflowTaskOrdering'
import { batchDispatch, runInDispatchBatch } from '../redux/dispatchBatch'
import { createFirstSnapshotPerformance } from '../performance/firestoreSnapshotPerformance'
import { enqueueOpenTasksBackgroundHydration } from './openTasksBackgroundQueue'
import { scheduleTaskColdStartCachePersist } from '../InitialLoad/taskColdStartCache'

export const TODAY_DATE = '0'

export const DATE_TASK_INDEX = 0
export const AMOUNT_TASKS_INDEX = 1
export const ESTIMATION_TASKS_INDEX = 2
export const MAIN_TASK_INDEX = 3
export const MENTION_TASK_INDEX = 4
export const SUGGESTED_TASK_INDEX = 5
export const WORKFLOW_TASK_INDEX = 6
export const OBSERVED_TASKS_INDEX = 7
export const STREAM_AND_USER_TASKS_INDEX = 8
export const ACTIVE_GOALS_INDEX = 9
// AT-2377: calendar-derived tasks own a bucket again, which is what gives them their own
// "Calendar" section in the task list. AT-2252 had merged them into MAIN_TASK_INDEX; three
// follow-up tickets (AT-2259, AT-2270, AT-2351) then fought to place a meeting sensibly inside
// a priority-sorted list, so the merge is reverted for calendar tasks.
//
// Inbox-summary EMAIL tasks are deliberately NOT restored to a bucket of their own: their old
// bucket (11) rendered nowhere on the open-tasks board, so they were invisible outside a goal.
// They stay in MAIN_TASK_INDEX.
export const CALENDAR_TASK_INDEX = 10
export const EMPTY_SECTION_INDEX = 11

export const NOT_PARENT_GOAL_INDEX = '0'

let userOpenTasks = {}
let userObservedTasks = {}
let streamAndUserOpenTasks = {}
const deferredSecondaryTaskStreamTimers = {}

// All Projects needs to discover the logged user's own tasks across several projects before
// secondary task sources are allowed to fill the Firestore connection. The assigned query is the
// common morning path; observed tasks follow shortly afterwards, while workstream tasks and empty
// goals wait until the first task rows have had time to commit.
export const DEFERRED_OBSERVED_TASK_STREAM_DELAY_MS = 1000
export const DEFERRED_REMAINING_TASK_STREAMS_DELAY_MS = 4500
export const DEFERRED_FULL_ASSIGNED_TASK_STREAM_DELAY_MS = 750

export const getForegroundOpenTasksLimit = ({
    deferSecondaryStreams,
    showLaterTasks,
    showSomedayTasks,
    assistantProfileMode,
    numberTodayTasks,
}) => {
    if (
        !deferSecondaryStreams ||
        showLaterTasks ||
        showSomedayTasks ||
        assistantProfileMode ||
        !Number.isFinite(numberTodayTasks) ||
        numberTodayTasks <= 0
    ) {
        return null
    }

    return Math.max(1, Math.floor(numberTodayTasks))
}

export const taskBelongsInOpenBoard = (task, assistantOwner, observed = false, assistantProfileMode = false) =>
    observed || !assistantOwner || assistantProfileMode || task?.workflowTask !== true

const cachedItemAsSnapshotDocument = item => ({
    id: item.id,
    data: () => item,
})

/**
 * A newly attached Firestore listener reports every matching document as `added`, even when the
 * task board retained the same document from an earlier listener. Replaying those additions over
 * retained mutable maps either leaves changed documents stale (parents de-dupe `added`) or can
 * leave documents that disappeared while the board was closed in the list forever.
 *
 * Complete listeners therefore reconcile their first usable snapshot as an authoritative set:
 * present cached documents become `modified`, new documents stay `added`, and cached documents
 * absent from the snapshot become synthetic `removed` changes. Limited foreground listeners use
 * `authoritative: false`; absence from a ten-document window says nothing about the remaining
 * project, but documents that are present still refresh immediately.
 */
export const reconcileInitialSnapshotChanges = (
    snapshotDocuments,
    knownItemsById,
    { authoritative = true, includeDocument = () => true } = {}
) => {
    const documents = snapshotDocuments || []
    const knownItems = knownItemsById || {}
    const snapshotIds = new Set()
    const changes = []

    documents.forEach(doc => {
        snapshotIds.add(doc.id)
        const knownItem = knownItems[doc.id]
        if (includeDocument(doc)) {
            changes.push({ type: knownItem ? 'modified' : 'added', doc })
        } else if (knownItem) {
            changes.push({ type: 'removed', doc: cachedItemAsSnapshotDocument(knownItem) })
        }
    })

    if (authoritative) {
        Object.entries(knownItems).forEach(([itemId, item]) => {
            if (!snapshotIds.has(itemId)) {
                changes.push({ type: 'removed', doc: cachedItemAsSnapshotDocument(item) })
            }
        })
    }

    return changes
}

const activeMilestoneEmptyGoals = {}

export const WATCHER_VARS_DEFAULT = {
    storedTasks: {},
    estimationByDate: {},
    amountOfTasksByDate: {},
    tasksMap: {
        observedTasksById: {},
        userTasksById: {},
        streamAndUserTasksById: {},
    },
    subtasksByParentId: {},
    subtasksMap: { observedSubtasksById: {}, userSubtasksById: {}, streamAndUserSubtasksById: {} },
}

//OPEN TASKS WATCHERS
export const unwatchOpenTasks = (projectId, currentUserId, { preserveData = false } = {}) => {
    const { globalDataByProject } = store.getState()
    if (!preserveData) {
        const nextGlobalDataByProject = { ...globalDataByProject }
        delete nextGlobalDataByProject[projectId]
        batchDispatch(setGlobalDataByProject(nextGlobalDataByProject))
    }

    unwatch(projectId, currentUserId, userOpenTasks)
    unwatch(projectId, currentUserId, userObservedTasks)
    unwatch(projectId, currentUserId, streamAndUserOpenTasks)

    const pendingTimers = deferredSecondaryTaskStreamTimers[projectId]?.[currentUserId] || []
    pendingTimers.forEach(cancel => cancel())
    if (deferredSecondaryTaskStreamTimers[projectId]) {
        delete deferredSecondaryTaskStreamTimers[projectId][currentUserId]
        if (Object.keys(deferredSecondaryTaskStreamTimers[projectId]).length === 0) {
            delete deferredSecondaryTaskStreamTimers[projectId]
        }
    }

    unwatchEmptyGoalsWatcher(projectId, currentUserId, activeMilestoneEmptyGoals)
}

export const watchOpenTasks = (
    projectId,
    callback,
    showLaterTasks,
    showSomedayTasks,
    keepMainDayData,
    instanceKey,
    assistantProfileMode = false,
    { trackConnectionHealth = true, deferSecondaryStreams = false } = {}
) =>
    // AT-2337: mounting "All projects" calls this once per project (~78x) and each
    // call fired 4 separate store notifications before a single task had loaded.
    // Firestore never delivers a snapshot synchronously from onSnapshot(), so the
    // batch closes before any watcher callback can run.
    runInDispatchBatch(() => {
        watchOpenTasksInternal(
            projectId,
            callback,
            showLaterTasks,
            showSomedayTasks,
            keepMainDayData,
            instanceKey,
            assistantProfileMode,
            trackConnectionHealth,
            deferSecondaryStreams
        )
    })

const watchOpenTasksInternal = (
    projectId,
    callback,
    showLaterTasks,
    showSomedayTasks,
    keepMainDayData,
    instanceKey,
    assistantProfileMode = false,
    trackConnectionHealth = true,
    deferSecondaryStreams = false
) => {
    const { currentUser, taskListWatchersVars, globalDataByProject } = store.getState()

    let storedTasks = {}
    let estimationByDate = {}
    let amountOfTasksByDate = {}
    let tasksMap = {
        observedTasksById: {},
        userTasksById: {},
        streamAndUserTasksById: {},
    }
    let goalsMap = {}

    let subtasksByParentId = {}
    let subtasksMap = { observedSubtasksById: {}, userSubtasksById: {}, streamAndUserSubtasksById: {} }

    // Reset vars at the beginning
    batchDispatch(
        setTaskListWatchersVars({
            ...taskListWatchersVars,
            storedTasks,
            estimationByDate,
            amountOfTasksByDate,
            tasksMap,
            subtasksByParentId,
            subtasksMap,
        })
    )

    if (keepMainDayData && globalDataByProject[projectId]) {
        const currentGlobalData = globalDataByProject[projectId]
        const mainDayDate = TODAY_DATE

        const storedTasksMainDay = currentGlobalData.storedTasks[mainDayDate]
        storedTasks = storedTasksMainDay ? { [mainDayDate]: storedTasksMainDay } : {}

        const estimationMainDay = currentGlobalData.estimationByDate[mainDayDate]
        estimationByDate = estimationMainDay ? { [mainDayDate]: estimationMainDay } : {}

        const amountOfTasksMainDay = currentGlobalData.amountOfTasksByDate[mainDayDate]
        amountOfTasksByDate = amountOfTasksMainDay ? { [mainDayDate]: amountOfTasksMainDay } : {}

        const date = moment()
        const endOfDay = date.endOf('day').valueOf()

        tasksMap = {
            observedTasksById: flow([
                Object.entries,
                arr =>
                    arr.filter(([key, task]) => {
                        return task.dueDateByObserversIds[currentUser.uid] <= endOfDay
                    }),
                Object.fromEntries,
            ])(currentGlobalData.tasksMap?.observedTasksById || {}),
            userTasksById: flow([
                Object.entries,
                arr =>
                    arr.filter(([key, task]) => {
                        return task.dueDate <= endOfDay
                    }),
                Object.fromEntries,
            ])(currentGlobalData.tasksMap?.userTasksById || {}),
            streamAndUserTasksById: flow([
                Object.entries,
                arr =>
                    arr.filter(([key, task]) => {
                        return task.dueDate <= endOfDay
                    }),
                Object.fromEntries,
            ])(currentGlobalData.tasksMap?.streamAndUserTasksById || {}),
        }

        goalsMap = flow([
            Object.entries,
            arr =>
                arr.filter(([key, goal]) => {
                    return (
                        goal.assigneesReminderDate[currentUser.uid] !== BACKLOG_DATE_NUMERIC &&
                        goal.assigneesReminderDate[currentUser.uid] <= endOfDay
                    )
                }),
            Object.fromEntries,
        ])(currentGlobalData.goalsMap || {})

        // The rendered snapshot keeps subtasks visible while this new listener reconnects. Keep
        // the listener-owned subtask indexes alongside it so the first foreground parent snapshot
        // cannot momentarily replace those rows with an empty subtask map.
        subtasksByParentId = currentGlobalData.subtasksByParentId || {}
        subtasksMap = currentGlobalData.subtasksMap || {
            observedSubtasksById: {},
            userSubtasksById: {},
            streamAndUserSubtasksById: {},
        }
    }

    unwatchOpenTasks(projectId, currentUser.uid)
    const globalData = {
        storedTasks,
        estimationByDate,
        amountOfTasksByDate,
        tasksMap,
        goalsMap,
        subtasksByParentId,
        subtasksMap,
    }

    batchDispatch(setGlobalDataByProject({ ...globalDataByProject, [projectId]: globalData }))

    const startObservedTasks = () => {
        const unsubscribe = watchUserOpenTasks(
            projectId,
            callback,
            storedTasks,
            estimationByDate,
            amountOfTasksByDate,
            tasksMap,
            true,
            instanceKey,
            showLaterTasks,
            showSomedayTasks,
            subtasksByParentId,
            subtasksMap,
            {
                assistantProfileMode: false,
                trackConnectionHealth,
            }
        )
        setProperty(userObservedTasks, [projectId, currentUser.uid, 'observed'], unsubscribe)
    }

    const startRemainingStreams = () => {
        watchStreamAndUserOpenTasksInBatches(
            projectId,
            callback,
            storedTasks,
            estimationByDate,
            amountOfTasksByDate,
            tasksMap,
            showLaterTasks,
            showSomedayTasks,
            subtasksByParentId,
            subtasksMap,
            trackConnectionHealth
        )

        watchEmptyGoals(
            projectId,
            callback,
            storedTasks,
            goalsMap,
            showLaterTasks,
            showSomedayTasks,
            estimationByDate,
            amountOfTasksByDate,
            trackConnectionHealth
        )
    }

    const deferredCancels = []
    const rememberDeferredCancel = cancel => {
        deferredCancels.push(cancel)
        if (!deferredSecondaryTaskStreamTimers[projectId]) deferredSecondaryTaskStreamTimers[projectId] = {}
        deferredSecondaryTaskStreamTimers[projectId][currentUser.uid] = deferredCancels
        return cancel
    }
    const forgetDeferredCancel = cancel => {
        const index = deferredCancels.indexOf(cancel)
        if (index >= 0) deferredCancels.splice(index, 1)
    }
    const schedule = (streamStarter, delay) => {
        let timer
        let cancelled = false
        const cancel = () => {
            if (cancelled) return
            cancelled = true
            clearTimeout(timer)
            forgetDeferredCancel(cancel)
        }
        timer = setTimeout(() => {
            if (cancelled) return
            cancelled = true
            forgetDeferredCancel(cancel)
            streamStarter()
        }, delay)
        return rememberDeferredCancel(cancel)
    }

    let secondaryStreamsScheduled = false
    const scheduleSecondaryStreams = () => {
        if (secondaryStreamsScheduled) return
        secondaryStreamsScheduled = true
        schedule(startObservedTasks, DEFERRED_OBSERVED_TASK_STREAM_DELAY_MS)
        schedule(startRemainingStreams, DEFERRED_REMAINING_TASK_STREAMS_DELAY_MS)
    }

    // Keep one assigned-task owner in the watcher map even while its implementation transitions
    // from a small top-level query to the complete realtime query. Unmounting the project cancels a
    // queued hydration and every listener that has already started.
    const assignedUnsubscribers = new Set()
    let assignedWatchersDisposed = false
    const registerAssignedUnsubscriber = unsubscribe => {
        if (assignedWatchersDisposed) unsubscribe()
        else assignedUnsubscribers.add(unsubscribe)
        return unsubscribe
    }
    const stopAssignedUnsubscriber = unsubscribe => {
        if (!assignedUnsubscribers.delete(unsubscribe)) return
        unsubscribe()
    }
    const stopAssignedWatchers = () => {
        if (assignedWatchersDisposed) return
        assignedWatchersDisposed = true
        assignedUnsubscribers.forEach(unsubscribe => unsubscribe())
        assignedUnsubscribers.clear()
    }
    setProperty(userOpenTasks, [projectId, currentUser.uid, 'assigned'], stopAssignedWatchers)

    const foregroundLimit = getForegroundOpenTasksLimit({
        deferSecondaryStreams,
        showLaterTasks,
        showSomedayTasks,
        assistantProfileMode,
        numberTodayTasks: store.getState().loggedUser.numberTodayTasks,
    })

    if (foregroundLimit) {
        let foregroundUnsubscribe
        let hydrationScheduled = false
        const scheduleCompleteAssignedTasks = () => {
            if (hydrationScheduled) return
            hydrationScheduled = true
            schedule(() => {
                const cancelHydration = enqueueOpenTasksBackgroundHydration(releaseHydrationSlot => {
                    const completeUnsubscribe = registerAssignedUnsubscriber(
                        watchUserOpenTasks(
                            projectId,
                            callback,
                            storedTasks,
                            estimationByDate,
                            amountOfTasksByDate,
                            tasksMap,
                            false,
                            instanceKey,
                            showLaterTasks,
                            showSomedayTasks,
                            subtasksByParentId,
                            subtasksMap,
                            {
                                assistantProfileMode,
                                trackConnectionHealth: false,
                                affectsLoadingState: false,
                                performanceSource: 'assigned_open_tasks_background',
                                onInitialSnapshotFailed: () => {
                                    releaseHydrationSlot()
                                    scheduleSecondaryStreams()
                                },
                                onInitialSnapshotDelivered: () => {
                                    releaseHydrationSlot()
                                    stopAssignedUnsubscriber(foregroundUnsubscribe)
                                    scheduleSecondaryStreams()
                                },
                            }
                        )
                    )
                    return completeUnsubscribe
                })
                rememberDeferredCancel(cancelHydration)
            }, DEFERRED_FULL_ASSIGNED_TASK_STREAM_DELAY_MS)
        }

        foregroundUnsubscribe = registerAssignedUnsubscriber(
            watchUserOpenTasks(
                projectId,
                callback,
                storedTasks,
                estimationByDate,
                amountOfTasksByDate,
                tasksMap,
                false,
                instanceKey,
                showLaterTasks,
                showSomedayTasks,
                subtasksByParentId,
                subtasksMap,
                {
                    assistantProfileMode,
                    trackConnectionHealth,
                    onInitialSnapshotDelivered: scheduleCompleteAssignedTasks,
                    onInitialSnapshotFailed: scheduleCompleteAssignedTasks,
                    queryLimit: foregroundLimit,
                    topLevelOnly: true,
                    performanceSource: 'assigned_open_tasks_foreground',
                }
            )
        )
    } else {
        registerAssignedUnsubscriber(
            watchUserOpenTasks(
                projectId,
                callback,
                storedTasks,
                estimationByDate,
                amountOfTasksByDate,
                tasksMap,
                false,
                instanceKey,
                showLaterTasks,
                showSomedayTasks,
                subtasksByParentId,
                subtasksMap,
                {
                    assistantProfileMode,
                    trackConnectionHealth,
                    onInitialSnapshotDelivered: deferSecondaryStreams ? scheduleSecondaryStreams : undefined,
                    onInitialSnapshotFailed: deferSecondaryStreams ? scheduleSecondaryStreams : undefined,
                }
            )
        )

        if (!deferSecondaryStreams) {
            startObservedTasks()
            startRemainingStreams()
        }
    }

    // Save vars at the end
    batchDispatch(
        setTaskListWatchersVars({
            ...taskListWatchersVars,
            storedTasks,
            estimationByDate,
            amountOfTasksByDate,
            tasksMap,
            subtasksByParentId,
            subtasksMap,
        })
    )
}

/**
 * AT-2342 - does this raw task document satisfy the SAME conditions as `getOpenTasksQuery`?
 *
 * An optimistically published task (`optimisticTaskCreate.js`) never passes through Firestore's
 * matcher, so this is the only thing standing between it and a list it does not belong in. It
 * has to mirror `getOpenTasksQuery` clause for clause: the pipeline below re-derives the day
 * bucket and the goal grouping, but it does NOT re-check `inDone`, `currentReviewerId`,
 * `observersIds` or `isPublicFor`, so a task that fails those would be inserted and then never
 * be echoed back to correct it - it would sit in the list until the watcher restarts.
 *
 * Keep the two functions edited together; `openTasksOptimisticCreate.test.js` pins the pairs
 * that actually differ (assignee, visibility, done, and each of the three date windows).
 */
export const matchesOpenTasksQuery = (
    taskData,
    { areObservedTasks, currentUserId, loggedUser, showLaterTasks, showSomedayTasks, endOfDay, endOfTomorrow }
) => {
    if (!taskData || taskData.inDone !== false) return false

    if (areObservedTasks) {
        return Array.isArray(taskData.observersIds) && taskData.observersIds.includes(currentUserId)
    }

    const { uid: loggedUserId, isAnonymous } = loggedUser
    const allowUserIds = isAnonymous ? [FEED_PUBLIC_FOR_ALL] : [FEED_PUBLIC_FOR_ALL, loggedUserId]

    if (taskData.currentReviewerId !== currentUserId) return false
    if (!Array.isArray(taskData.isPublicFor) || !taskData.isPublicFor.some(id => allowUserIds.includes(id))) {
        return false
    }

    const { dueDate } = taskData
    if (!Number.isFinite(dueDate)) return false

    // The three expand states of getOpenTasksQuery, in the same order.
    if (!showLaterTasks && !showSomedayTasks) return dueDate <= endOfDay
    if (showLaterTasks && !showSomedayTasks) {
        return endOfTomorrow
            ? dueDate <= endOfTomorrow && dueDate < BACKLOG_DATE_NUMERIC
            : dueDate < BACKLOG_DATE_NUMERIC
    }
    return true
}

const getOpenTasksQuery = (
    projectId,
    areObservedTasks,
    showLaterTasks,
    showSomedayTasks,
    currentUserId,
    loggedUser,
    endOfDay,
    endOfTomorrow,
    { queryLimit = null, topLevelOnly = false } = {}
) => {
    const { uid: loggedUserId, isAnonymous } = loggedUser
    const allowUserIds = isAnonymous ? [FEED_PUBLIC_FOR_ALL] : [FEED_PUBLIC_FOR_ALL, loggedUserId]
    const accessReaderId = allowUserIds[allowUserIds.length - 1]
    let query = getDb().collection(`items/${projectId}/tasks`)
    if (areObservedTasks) {
        query = query.where(getRoleIdsVisibleToField(String(accessReaderId)), 'array-contains', currentUserId)
    } else {
        query = query
            .where('inDone', '==', false)
            .where('currentReviewerId', '==', currentUserId)
            .where('readerIds', 'array-contains', accessReaderId)

        // The foreground All Projects query needs rows, not child documents. Subtasks are loaded by
        // the complete background listener and then appear underneath their already-visible parent.
        if (topLevelOnly) query = query.where('parentId', '==', null)

        // Support three expand states:
        // State 0 (both false): only today (dueDate <= endOfDay)
        // State 1 (showLaterTasks=true but showSomedayTasks=false): today + tomorrow (dueDate <= endOfTomorrow)
        // State 2 (both true): all tasks including someday (no date filter)
        if (!showLaterTasks && !showSomedayTasks) {
            // State 0: Only today
            query = query.where('dueDate', '<=', endOfDay)
        } else if (showLaterTasks && !showSomedayTasks) {
            // State 1: Today + tomorrow only (new behavior)
            if (endOfTomorrow) {
                query = query.where('dueDate', '<=', endOfTomorrow).where('dueDate', '<', BACKLOG_DATE_NUMERIC)
            } else {
                // Fallback to old behavior (all future tasks) if endOfTomorrow not provided
                query = query.where('dueDate', '<', BACKLOG_DATE_NUMERIC)
            }
        }
        // State 2 (both true): No date filter, fetch all tasks including someday
    }
    if (Number.isInteger(queryLimit) && queryLimit > 0) query = query.limit(queryLimit)
    return query
}

const watchUserOpenTasks = (
    projectId,
    callback,
    storedTasks,
    estimationByDate,
    amountOfTasksByDate,
    tasksMap,
    areObservedTasks,
    instanceKey,
    showLaterTasks,
    showSomedayTasks,
    subtasksByParentId,
    subtasksMap,
    {
        assistantProfileMode = false,
        trackConnectionHealth = true,
        onInitialSnapshotDelivered,
        onInitialSnapshotFailed,
        queryLimit = null,
        topLevelOnly = false,
        affectsLoadingState = true,
        performanceSource,
    } = {}
) => {
    const { currentUser, loggedUser } = store.getState()
    const currentUserId = currentUser.uid
    const assistantOwner = !!currentUser.temperature

    const date = moment()
    const endOfDay = date.endOf('day').valueOf()
    const endOfTomorrow = moment().endOf('day').add(1, 'day').valueOf()
    const dayDateFormated = TODAY_DATE

    let query = getOpenTasksQuery(
        projectId,
        areObservedTasks,
        showLaterTasks,
        showSomedayTasks,
        currentUserId,
        loggedUser,
        endOfDay,
        endOfTomorrow,
        { queryLimit, topLevelOnly }
    )

    let cacheChanges = []
    let initialSnapshotDelivered = false
    // AT-2500 - ids this watcher rendered from an optimistic publication and has NOT yet seen in a
    // delivered query snapshot. Bounded by construction: an id is added when the row is inserted
    // and removed when a non-buffered snapshot's result set names it, so in steady state this is
    // empty and during a create it lives only through the projection round trip. Declared up here
    // rather than beside the subscriber that fills it, because the snapshot handler reads it.
    const unconfirmedOptimisticTaskIds = new Set()
    const gate = createCachedSnapshotGate(() => handleOpenTasksSnapshot, {
        trackConnectionHealth,
        loadingSource: !areObservedTasks && affectsLoadingState ? 'open_tasks' : undefined,
        onError: error => {
            snapshotPerformance.fail()
            if (!initialSnapshotDelivered) onInitialSnapshotFailed?.(error)
        },
    })
    const snapshotPerformance = createFirstSnapshotPerformance(
        {
            object_type: 'tasks',
            scope: 'project',
            source: performanceSource || (areObservedTasks ? 'observed_open_tasks' : 'assigned_open_tasks'),
        },
        { sampleRate: 0.02 }
    )
    function handleOpenTasksSnapshot(querySnapshot) {
        const liveChanges = querySnapshot
            .docChanges()
            .filter(change =>
                taskBelongsInOpenBoard(change.doc.data(), assistantOwner, areObservedTasks, assistantProfileMode)
            )

        const buffered = gate.shouldBuffer(querySnapshot)
        snapshotPerformance.observe(querySnapshot, buffered)
        if (buffered) {
            cacheChanges = [...cacheChanges, ...liveChanges]
        } else {
            // AT-2539 - only a snapshot this handler is actually delivering can take ownership of
            // an optimistic row. A cached snapshot held behind the gate has not updated the list
            // yet; confirming it here would stop document-level reconciliation while the UI still
            // held the create-time copy. Read `docs`, not `docChanges()`, because the gate flush
            // deliberately has an empty change list but carries the authoritative result set.
            if (unconfirmedOptimisticTaskIds.size > 0) {
                ;(querySnapshot.docs || []).forEach(doc => {
                    unconfirmedOptimisticTaskIds.delete(doc.id)
                    confirmOptimisticTaskForSubscriber(projectId, doc.id, handleOptimisticTaskChange)
                })
            }

            const isInitialSnapshot = !initialSnapshotDelivered
            const taskItems = areObservedTasks ? tasksMap.observedTasksById : tasksMap.userTasksById
            const subtaskItems = areObservedTasks ? subtasksMap.observedSubtasksById : subtasksMap.userSubtasksById
            const changes = isInitialSnapshot
                ? reconcileInitialSnapshotChanges(
                      querySnapshot.docs,
                      { ...taskItems, ...subtaskItems },
                      {
                          // A limited foreground window can refresh documents it contains, but only the
                          // subsequent complete listener may remove retained documents absent from it.
                          authoritative: !Number.isInteger(queryLimit) || queryLimit <= 0,
                          includeDocument: doc =>
                              taskBelongsInOpenBoard(
                                  doc.data(),
                                  assistantOwner,
                                  areObservedTasks,
                                  assistantProfileMode
                              ),
                      }
                  )
                : liveChanges
            if (isInitialSnapshot) cacheChanges = []
            deliverOpenTasksChanges(changes)
            if (isInitialSnapshot) {
                initialSnapshotDelivered = true
                onInitialSnapshotDelivered?.()
            }
        }
    }

    /**
     * The delivery half of the snapshot handler, split out so AT-2342's optimistic insert can
     * reach it without impersonating a Firestore snapshot.
     *
     * Optimistic publications neither flush buffered snapshots nor complete the
     * initial read. Only the snapshot gate can release this listener's loading owner.
     */
    function deliverOpenTasksChanges(changes, { optimistic = false } = {}) {
        // AT-2337: one snapshot used to produce ~10 separate store notifications,
        // and "All projects" runs this handler once per project (~78x on a heavy
        // account). Collect them into a single array dispatch instead - same
        // actions, same order, same resulting state, one subscriber pass.
        runInDispatchBatch(() => {
            const mergedChanges = optimistic ? changes : [...cacheChanges, ...changes]

            let subtasks = { ...subtasksByParentId }

            if (mergedChanges.length > 0) {
                const { openTasksArray, subtasksByTasks } = processTaskChanges(
                    projectId,
                    mergedChanges,
                    loggedUser,
                    currentUserId,
                    endOfDay,
                    storedTasks,
                    estimationByDate,
                    amountOfTasksByDate,
                    tasksMap,
                    areObservedTasks,
                    false,
                    dayDateFormated,
                    showLaterTasks,
                    showSomedayTasks,
                    subtasksByParentId,
                    subtasksMap,
                    assistantProfileMode
                )
                subtasks = subtasksByTasks
                callback(openTasksArray, !areObservedTasks)
                batchDispatch(setOpenTasksMap(projectId, { ...tasksMap.observedTasksById, ...tasksMap.userTasksById }))
            } else if (Object.keys(storedTasks).length === 0) {
                callback([[dayDateFormated, 0, 0, [], [], [], [], [], [], [], [], []]], !areObservedTasks)
                batchDispatch(setOpenTasksMap(projectId, {}))
            } else if (areObservedTasks) {
                batchDispatch(updateInitialLoadingEndObservedTasks(instanceKey, true))
            } else {
                // The observed stream may populate storedTasks before the assigned
                // stream delivers an empty first snapshot. That empty assigned
                // stream is still ready; without this flag a staged project waits
                // for the five-second safety timeout despite already having data.
                batchDispatch(updateInitialLoadingEndOpenTasks(instanceKey, true))
            }

            batchDispatch(
                setOpenSubtasksMap(projectId, {
                    ...subtasksMap.observedSubtasksById,
                    ...subtasksMap.userSubtasksById,
                    ...subtasksMap.streamAndUserSubtasksById,
                })
            )

            batchDispatch(updateSubtaskByTask(instanceKey, subtasks))

            if (!optimistic) cacheChanges = []
        })
    }

    // AT-2342 - render a task this user just created without waiting for it to come back from
    // Firestore. `matchesOpenTasksQuery` is what keeps a task out of a list it does not belong
    // in, since nothing else re-checks the query clauses.
    const isTaskInThisView = taskData =>
        matchesOpenTasksQuery(taskData, {
            areObservedTasks,
            currentUserId,
            loggedUser,
            showLaterTasks,
            showSomedayTasks,
            endOfDay,
            endOfTomorrow,
        }) && taskBelongsInOpenBoard(taskData, assistantOwner, areObservedTasks, assistantProfileMode)

    const knownTaskIds = () => (areObservedTasks ? tasksMap.observedTasksById : tasksMap.userTasksById)

    // Absence from a LIMITED foreground window says nothing - the task may simply be outside the
    // ten documents it asked for. Only a complete listener may conclude "not in this query", the
    // same rule `reconcileInitialSnapshotChanges` applies to its own synthetic removals.
    const isAuthoritativeListener = !Number.isInteger(queryLimit) || queryLimit <= 0

    /**
     * The settlement handler. The server has accepted the create and `change.doc.data()` is the
     * document as it stands now, so this is a re-evaluation of a row we put on screen ourselves -
     * NOT a report that Firestore has nothing to say about it.
     *
     * The distinction is the whole of the AT-2500 follow-up. This used to retire the row purely
     * because no snapshot had mentioned the task yet, on the reasoning that a local `added` always
     * beats the server ack. It never does here: every query this watcher runs filters on
     * `readerIds` / `roleIdsVisibleTo.<reader>`, which the access rules forbid a client to write,
     * so a locally created task matches NOTHING locally and is first seen only once
     * `synchronizeAccessProjection` has stamped the projection server-side and it has travelled
     * back. The ack therefore won that race on every create, and the row blinked out for the
     * duration of a round trip before reappearing.
     *
     * Deciding on the document instead has no race in it:
     *   - still in this view  -> keep it, updating in place through the normal pipeline so the row
     *                            picks up whatever changed (a rename, a postpone to later today)
     *                            without ever leaving the list;
     *   - no longer in view   -> remove it, which is the original AT-2500 case (postponed straight
     *                            out of `dueDate <= endOfDay`, so no echo is ever coming);
     *   - no document at all  -> no verdict, so leave the row exactly as it is.
     *
     * Removal reuses the retained copy as the change payload, because `deleteTask` derives the
     * bucket to clear from the change's own data and the retained copy is precisely what decided
     * the bucket the row sits in.
     */
    const settleOptimisticTask = (taskId, latestTaskData) => {
        if (!unconfirmedOptimisticTaskIds.has(taskId)) return

        const retainedTask = knownTaskIds()[taskId]
        if (!retainedTask) {
            unconfirmedOptimisticTaskIds.delete(taskId)
            confirmOptimisticTaskForSubscriber(projectId, taskId, handleOptimisticTaskChange)
            return
        }

        // No verdict: the cache could not say what the task looks like. Keep the row and keep it
        // unconfirmed, so the real snapshot still reconciles it when the projection lands.
        if (!latestTaskData) return

        if (isTaskInThisView(latestTaskData)) {
            // The row stays; it is refreshed in place. `modified` is a delete-then-add inside ONE
            // delivery, i.e. a single recompute and a single callback - the list never renders a
            // frame without the task. Still unconfirmed: only a real snapshot confirms.
            deliverOpenTasksChanges([{ type: 'modified', doc: { id: taskId, data: () => latestTaskData } }], {
                optimistic: true,
            })
            return
        }

        if (!isAuthoritativeListener) return
        unconfirmedOptimisticTaskIds.delete(taskId)
        deliverOpenTasksChanges([{ type: 'removed', doc: { id: taskId, data: () => retainedTask } }], {
            optimistic: true,
        })
        confirmOptimisticTaskForSubscriber(projectId, taskId, handleOptimisticTaskChange)
    }

    function handleOptimisticTaskChange(change) {
        if (change.type === OPTIMISTIC_TASK_SETTLED) {
            settleOptimisticTask(change.doc.id, change.doc.data())
            return false
        }

        const taskData = change.doc.data()
        if (!isTaskInThisView(taskData)) return false

        // Removal must be idempotent: a rejected write is rolled back twice over - once by the
        // explicit publication and once by Firestore reverting its own local mutation - and
        // `deleteTask` decrements the per-day task count and estimation total every time it runs.
        // A subtask lives in `subtasksMap`, not here, so let the pipeline judge that case itself.
        if (change.type === OPTIMISTIC_TASK_REMOVED && !taskData.parentId && !knownTaskIds()[change.doc.id]) {
            return false
        }

        change.type === OPTIMISTIC_TASK_REMOVED
            ? unconfirmedOptimisticTaskIds.delete(change.doc.id)
            : unconfirmedOptimisticTaskIds.add(change.doc.id)

        deliverOpenTasksChanges([change], { optimistic: true })
        return change.type !== OPTIMISTIC_TASK_REMOVED
    }

    const unsubOptimistic = subscribeToOptimisticTaskCreates(projectId, handleOptimisticTaskChange)

    let unsub
    try {
        unsub = gate.subscribe(query)
    } catch (error) {
        unsubOptimistic()
        throw error
    }
    let unsubscribed = false
    const unsubAll = () => {
        if (unsubscribed) return
        unsubscribed = true
        unsubOptimistic()
        snapshotPerformance.cancel()
        unsub()
    }

    return unsubAll
}

export const getTaskTypeIndex = (task, areObservedTasks, areStreamAndUserTasks, assistantProfileMode = false) => {
    const { genericData, suggestedBy, userIds, calendarData } = task
    if (areObservedTasks) return OBSERVED_TASKS_INDEX
    if (areStreamAndUserTasks) return STREAM_AND_USER_TASKS_INDEX
    // The assistant page intentionally has one timeline. A workflow task that is currently being
    // handled by this assistant must therefore render like a regular task even when its workflow
    // history already contains multiple reviewers.
    if (assistantProfileMode && task.workflowTask === true) return MAIN_TASK_INDEX
    if (genericData) return MENTION_TASK_INDEX
    if (userIds.length > 1) return WORKFLOW_TASK_INDEX
    if (suggestedBy) return SUGGESTED_TASK_INDEX
    // AT-2377: a synced meeting belongs in the Calendar section, not in the priority-sorted list.
    if (calendarData) return CALENDAR_TASK_INDEX
    // Inbox-summary email tasks stay here on purpose - see the EMAIL note by the constants.
    return MAIN_TASK_INDEX
}

const processTaskChange = (
    projectId,
    loggedUser,
    currentUserId,
    endOfDay,
    storedTasks,
    estimationByDate,
    amountOfTasksByDate,
    tasksMap,
    areObservedTasks,
    areStreamAndUserTasks,
    dayDateFormated,
    showLaterTasks,
    showSomedayTasks,
    listsToSort,
    changeType,
    task,
    assistantProfileMode = false
) => {
    const { uid: loggedUserId } = loggedUser

    const {
        estimations,
        stepHistory,
        suggestedBy,
        dueDate,
        dueDateByObserversIds,
        estimationsByObserverIds,
        userId,
        userIds,
        isPublicFor,
        parentGoalId,
        parentGoalIsPublicFor,
    } = task

    if (parentGoalId && !Array.isArray(parentGoalIsPublicFor)) {
        console.warn('[OpenTasks] parentGoalIsPublicFor missing/invalid', {
            taskId: task && task.id,
            parentGoalId,
            parentGoalIsPublicFor,
        })
    }

    const safeParentGoalIsPublicFor = Array.isArray(parentGoalIsPublicFor) ? parentGoalIsPublicFor : []

    const taskParentGoalId =
        parentGoalId &&
        (safeParentGoalIsPublicFor.includes(FEED_PUBLIC_FOR_ALL) || safeParentGoalIsPublicFor.includes(loggedUserId))
            ? parentGoalId
            : NOT_PARENT_GOAL_INDEX
    const taskInBacklog = areObservedTasks
        ? dueDateByObserversIds[currentUserId] === Number.MAX_SAFE_INTEGER
        : dueDate === Number.MAX_SAFE_INTEGER
    const taskDueDate = areObservedTasks ? dueDateByObserversIds[currentUserId] : dueDate
    const taskIsTodayOrOverdue = taskDueDate <= endOfDay
    const taskIsLater = taskDueDate > endOfDay && !taskInBacklog

    const date = taskIsTodayOrOverdue
        ? dayDateFormated
        : taskInBacklog
          ? BACKLOG_DATE_STRING
          : moment(taskDueDate).format('YYYYMMDD')
    const taskTypeIndex = getTaskTypeIndex(task, areObservedTasks, areStreamAndUserTasks, assistantProfileMode)
    const currentStepId = stepHistory[stepHistory.length - 1]

    const estimation = areObservedTasks
        ? estimationsByObserverIds[currentUserId]
            ? estimationsByObserverIds[currentUserId]
            : 0
        : estimations[currentStepId]
          ? estimations[currentStepId]
          : 0

    let innerGroupKey = ''
    if (taskTypeIndex === WORKFLOW_TASK_INDEX) {
        innerGroupKey = currentStepId
    } else if (taskTypeIndex === SUGGESTED_TASK_INDEX) {
        innerGroupKey = suggestedBy
    } else if (taskTypeIndex === OBSERVED_TASKS_INDEX) {
        innerGroupKey = userId
    } else if (taskTypeIndex === STREAM_AND_USER_TASKS_INDEX) {
        innerGroupKey = userId
    }
    const sortListKey = `${date}+${taskTypeIndex}+${innerGroupKey}+${taskParentGoalId}`
    const sortListValue = { date, taskTypeIndex, innerGroupKey, taskParentGoalId }

    let needToProcessTheTask = task.inDone === false
    if (taskTypeIndex === OBSERVED_TASKS_INDEX) {
        if (!Array.isArray(isPublicFor)) {
            console.warn('[OpenTasks] isPublicFor missing/invalid on task', { taskId: task && task.id, isPublicFor })
        }

        const safeIsPublicFor = Array.isArray(isPublicFor) ? isPublicFor : []

        const isPublicForLoggedUser =
            safeIsPublicFor.includes(FEED_PUBLIC_FOR_ALL) ||
            (!loggedUser.isAnonymous && safeIsPublicFor.includes(loggedUserId))

        const needToBeListedInThisDate = showSomedayTasks || taskIsTodayOrOverdue || (showLaterTasks && taskIsLater)
        needToProcessTheTask = task.inDone === false && isPublicForLoggedUser && needToBeListedInThisDate
    }

    if (areStreamAndUserTasks) {
        needToProcessTheTask = task.inDone === false && userIds.length === 1
    }

    const addTask = () => {
        if (needToProcessTheTask) {
            if (!storedTasks[date]) {
                storedTasks[date] = {}
                estimationByDate[date] = ESTIMATION_0_MIN
                amountOfTasksByDate[date] = 0
            }

            estimationByDate[date] += getEstimationRealValue(projectId, estimation)

            amountOfTasksByDate[date]++

            if (innerGroupKey) {
                if (!storedTasks[date][taskTypeIndex]) storedTasks[date][taskTypeIndex] = {}
                if (!storedTasks[date][taskTypeIndex][innerGroupKey])
                    storedTasks[date][taskTypeIndex][innerGroupKey] = {}
                if (!storedTasks[date][taskTypeIndex][innerGroupKey][taskParentGoalId])
                    storedTasks[date][taskTypeIndex][innerGroupKey][taskParentGoalId] = []
                storedTasks[date][taskTypeIndex][innerGroupKey][taskParentGoalId].push(task)
                if (storedTasks[date][taskTypeIndex][innerGroupKey][taskParentGoalId].length > 1)
                    listsToSort[sortListKey] = sortListValue
            } else {
                if (!storedTasks[date][taskTypeIndex]) storedTasks[date][taskTypeIndex] = {}
                if (!storedTasks[date][taskTypeIndex][taskParentGoalId])
                    storedTasks[date][taskTypeIndex][taskParentGoalId] = []
                storedTasks[date][taskTypeIndex][taskParentGoalId].push(task)
                if (storedTasks[date][taskTypeIndex][taskParentGoalId].length > 1)
                    listsToSort[sortListKey] = sortListValue
            }

            //THE WORKFLOW ALGORITHM IS UPDATING THE TASK DIRECTLY (BAD PRACTICE) AND WE NEED TO CLONE THE TASK FOR NOT BEEN AFFECTED BY THIS
            //IS THE WORKLFOW IS REFACTORED WE CAN REMOVE TEH CLONE AND PUT THE TASK DIRECTLY
            if (areObservedTasks) {
                tasksMap.observedTasksById[task.id] = { ...cloneDeep(task), isObservedTask: true }
            } else if (areStreamAndUserTasks) {
                tasksMap.streamAndUserTasksById[task.id] = { ...cloneDeep(task), isObservedTask: false }
            } else {
                tasksMap.userTasksById[task.id] = { ...cloneDeep(task), isObservedTask: false }
            }
        }
    }

    const deleteTask = (date, taskTypeIndex, innerGroupKey, taskParentGoalId, estimation, notDeleteParentGoal) => {
        estimationByDate[date] -= getEstimationRealValue(projectId, estimation)

        amountOfTasksByDate[date]--

        if (areObservedTasks) {
            delete tasksMap.observedTasksById[task.id]
        } else if (areStreamAndUserTasks) {
            delete tasksMap.streamAndUserTasksById[task.id]
        } else {
            delete tasksMap.userTasksById[task.id]
        }

        const cleanDateData = amountTypesInDate => {
            if (amountTypesInDate === 0) {
                delete storedTasks[date]
                delete estimationByDate[date]
                delete amountOfTasksByDate[date]
            }
        }

        let amountTypesInDate
        if (innerGroupKey) {
            storedTasks[date][taskTypeIndex][innerGroupKey][taskParentGoalId] = storedTasks[date][taskTypeIndex][
                innerGroupKey
            ][taskParentGoalId].filter(taskItem => taskItem.id !== task.id)
            if (!notDeleteParentGoal && storedTasks[date][taskTypeIndex][innerGroupKey][taskParentGoalId].length === 0)
                delete storedTasks[date][taskTypeIndex][innerGroupKey][taskParentGoalId]
            if (Object.keys(storedTasks[date][taskTypeIndex][innerGroupKey]).length === 0)
                delete storedTasks[date][taskTypeIndex][innerGroupKey]
            const amountStepsInType = Object.keys(storedTasks[date][taskTypeIndex]).length
            if (amountStepsInType <= 1) delete listsToSort[sortListKey]
            if (Object.keys(storedTasks[date][taskTypeIndex]).length === 0) delete storedTasks[date][taskTypeIndex]
            amountTypesInDate = Object.keys(storedTasks[date]).length
            cleanDateData(amountTypesInDate)
        } else {
            storedTasks[date][taskTypeIndex][taskParentGoalId] = storedTasks[date][taskTypeIndex][
                taskParentGoalId
            ].filter(taskItem => taskItem.id !== task.id)
            if (!notDeleteParentGoal && storedTasks[date][taskTypeIndex][taskParentGoalId].length === 0)
                delete storedTasks[date][taskTypeIndex][taskParentGoalId]
            if (Object.keys(storedTasks[date][taskTypeIndex]).length === 0) delete storedTasks[date][taskTypeIndex]
            amountTypesInDate = Object.keys(storedTasks[date]).length
            if (amountTypesInDate <= 1) delete listsToSort[sortListKey]
            cleanDateData(amountTypesInDate)
        }
    }

    //PROCESS THE CHANGE
    // AT-2500: `added` is reconciled exactly like `modified`, never de-duped away. An `added` for a
    // task this watcher already holds is not a duplicate to discard - it is the authoritative
    // document arriving for a row that was put on screen from somewhere else, i.e. AT-2342's
    // optimistic insert. The two payloads are only byte-identical while nobody edits the task in
    // the gap between `publishOptimisticTaskCreated` and the echo; postponing a task the instant
    // it is created lands squarely in that gap, and the old `if (notExistTask) addTask()` guard
    // then threw the corrected document away and left the row in today's bucket for ever.
    //
    // Reconciling instead cannot duplicate the row: `wasNeedToProcessTheTask` is false for a task
    // that is genuinely new, so this collapses to the plain `addTask()` the `added` branch used to
    // do, and for a known task it is a delete-then-add, which is exactly one entry either way.
    // `reconcileInitialSnapshotChanges` already rewrites known `added`s to `modified` for this very
    // reason - this makes the live path agree with the initial-snapshot path.
    if (changeType === 'modified' || changeType === 'added') {
        const oldTask = areObservedTasks
            ? tasksMap.observedTasksById[task.id]
            : areStreamAndUserTasks
              ? tasksMap.streamAndUserTasksById[task.id]
              : tasksMap.userTasksById[task.id]

        const wasNeedToProcessTheTask = !!oldTask
        if (wasNeedToProcessTheTask) {
            const oldTaskInBacklog = areObservedTasks
                ? oldTask.dueDateByObserversIds[currentUserId] === Number.MAX_SAFE_INTEGER
                : oldTask.dueDate === Number.MAX_SAFE_INTEGER
            const oldTaskDueDate = areObservedTasks ? oldTask.dueDateByObserversIds[currentUserId] : oldTask.dueDate
            const oldTaskIsTodayOrOverdue = oldTaskDueDate <= endOfDay && !oldTaskInBacklog

            const oldDate = oldTaskIsTodayOrOverdue
                ? dayDateFormated
                : oldTaskInBacklog
                  ? BACKLOG_DATE_STRING
                  : moment(oldTaskDueDate).format('YYYYMMDD')

            const oldTaskTypeIndex = getTaskTypeIndex(
                oldTask,
                areObservedTasks,
                areStreamAndUserTasks,
                assistantProfileMode
            )
            const oldStepHistory = oldTask.stepHistory
            const oldCurrentStepId = oldStepHistory[oldStepHistory.length - 1]
            const oldSuggestedBy = oldTask.suggestedBy
            const oldEstimation = areObservedTasks
                ? oldTask.estimationsByObserverIds[currentUserId]
                    ? oldTask.estimationsByObserverIds[currentUserId]
                    : 0
                : oldTask.estimations[oldCurrentStepId]
                  ? oldTask.estimations[oldCurrentStepId]
                  : 0
            const oldUserId = oldTask.userId
            if (oldTask.parentGoalId && !Array.isArray(oldTask.parentGoalIsPublicFor)) {
                console.warn('[OpenTasks] oldTask.parentGoalIsPublicFor missing/invalid', {
                    taskId: oldTask && oldTask.id,
                    parentGoalId: oldTask.parentGoalId,
                    parentGoalIsPublicFor: oldTask.parentGoalIsPublicFor,
                })
            }
            const safeOldParentGoalIsPublicFor = Array.isArray(oldTask.parentGoalIsPublicFor)
                ? oldTask.parentGoalIsPublicFor
                : []
            const oldTaskParentGoalId =
                oldTask.parentGoalId &&
                (safeOldParentGoalIsPublicFor.includes(FEED_PUBLIC_FOR_ALL) ||
                    safeOldParentGoalIsPublicFor.includes(loggedUserId))
                    ? oldTask.parentGoalId
                    : NOT_PARENT_GOAL_INDEX

            let oldInnerGroupKey = ''
            if (oldTaskTypeIndex === WORKFLOW_TASK_INDEX) {
                oldInnerGroupKey = oldCurrentStepId
            } else if (oldTaskTypeIndex === SUGGESTED_TASK_INDEX) {
                oldInnerGroupKey = oldSuggestedBy
            } else if (oldTaskTypeIndex === OBSERVED_TASKS_INDEX) {
                oldInnerGroupKey = oldUserId
            } else if (oldTaskTypeIndex === STREAM_AND_USER_TASKS_INDEX) {
                oldInnerGroupKey = oldUserId
            }
            deleteTask(
                oldDate,
                oldTaskTypeIndex,
                oldInnerGroupKey,
                oldTaskParentGoalId,
                oldEstimation,
                oldTaskParentGoalId === taskParentGoalId &&
                    oldInnerGroupKey === innerGroupKey &&
                    oldTaskTypeIndex === taskTypeIndex &&
                    oldDate === date
            )
        }
        addTask()
    } else {
        if (needToProcessTheTask) deleteTask(date, taskTypeIndex, innerGroupKey, taskParentGoalId, estimation, false)
    }
}

const sortTasksListThatHaveNewTasks = (storedTasks, listsToSort) => {
    for (const sortListKey in listsToSort) {
        const { date, taskTypeIndex, innerGroupKey, taskParentGoalId } = listsToSort[sortListKey]
        if (
            taskTypeIndex === WORKFLOW_TASK_INDEX ||
            taskTypeIndex === SUGGESTED_TASK_INDEX ||
            taskTypeIndex === OBSERVED_TASKS_INDEX ||
            taskTypeIndex === STREAM_AND_USER_TASKS_INDEX
        ) {
            const taskList = orderBy(
                storedTasks[date][taskTypeIndex][innerGroupKey][taskParentGoalId],
                'sortIndex',
                'desc'
            )
            storedTasks[date][taskTypeIndex][innerGroupKey][taskParentGoalId] =
                taskTypeIndex === WORKFLOW_TASK_INDEX ? taskList : sortTasksByPriority(taskList)
        } else {
            const taskList = orderBy(storedTasks[date][taskTypeIndex][taskParentGoalId], 'sortIndex', 'desc')
            // AT-2377: the Calendar section orders its own tasks chronologically by event start, so
            // priority must not reshuffle them here.
            storedTasks[date][taskTypeIndex][taskParentGoalId] =
                taskTypeIndex === CALENDAR_TASK_INDEX ? taskList : sortTasksByPriority(taskList)
        }
    }
}

const generateOpenTasksArray = (storedTasks, dayDateFormated, amountOfTasksByDate, estimationByDate) => {
    const tasksByDateAndStep = Object.entries(storedTasks).sort((a, b) => a[0] - b[0])
    const openTasksArray = storedTasks[dayDateFormated]
        ? []
        : [[dayDateFormated, 0, 0, [], [], [], [], [], [], [], [], []]]

    for (let i = 0; i < tasksByDateAndStep.length; i++) {
        const dateElement = tasksByDateAndStep[i]
        const date = dateElement[0]
        const taskByType = dateElement[1]
        const amountTasks = amountOfTasksByDate[date]
        const estimationTasks = estimationByDate[date]
        const mainTasks = taskByType[MAIN_TASK_INDEX] ? Object.entries(taskByType[MAIN_TASK_INDEX]) : []
        const mentionTasks = taskByType[MENTION_TASK_INDEX] ? Object.entries(taskByType[MENTION_TASK_INDEX]) : []
        const activeGoals = taskByType[ACTIVE_GOALS_INDEX] ? taskByType[ACTIVE_GOALS_INDEX] : []

        const suggestedTasks = []
        if (taskByType[SUGGESTED_TASK_INDEX]) {
            const suggestedTasksBySuggested = Object.entries(taskByType[SUGGESTED_TASK_INDEX])
            for (let n = 0; n < suggestedTasksBySuggested.length; n++) {
                const suggestedId = suggestedTasksBySuggested[n][0]
                const goalsElements = suggestedTasksBySuggested[n][1]
                const goalsElementsArray = Object.entries(goalsElements)
                suggestedTasks.push([suggestedId, goalsElementsArray])
            }
        }

        const workflowTasks = []
        if (taskByType[WORKFLOW_TASK_INDEX]) {
            workflowTasks.push(...buildWorkflowTaskGroups(taskByType[WORKFLOW_TASK_INDEX]))
        }

        const observedTasks = []
        if (taskByType[OBSERVED_TASKS_INDEX]) {
            const observedTasksByAssignee = Object.entries(taskByType[OBSERVED_TASKS_INDEX])
            for (let n = 0; n < observedTasksByAssignee.length; n++) {
                const assigneeId = observedTasksByAssignee[n][0]
                const goalsElements = observedTasksByAssignee[n][1]
                const goalsElementsArray = Object.entries(goalsElements)
                observedTasks.push([assigneeId, goalsElementsArray])
            }
        }

        const streamAndUserTasks = []
        if (taskByType[STREAM_AND_USER_TASKS_INDEX]) {
            const streamAndUserTasksByAssignee = Object.entries(taskByType[STREAM_AND_USER_TASKS_INDEX])
            for (let n = 0; n < streamAndUserTasksByAssignee.length; n++) {
                const assigneeId = streamAndUserTasksByAssignee[n][0]
                const goalsElements = streamAndUserTasksByAssignee[n][1]
                const goalsElementsArray = Object.entries(goalsElements)
                streamAndUserTasks.push([assigneeId, goalsElementsArray])
            }
        }

        const calendarTasks = taskByType[CALENDAR_TASK_INDEX] ? Object.entries(taskByType[CALENDAR_TASK_INDEX]) : []

        // A goal counts as empty only when NOTHING renders under it. Calendar tasks are grouped by
        // goal inside the Calendar section, so a goal holding only meetings has visible content and
        // must not also be listed as an empty goal - it would render its header twice.
        const goalIdsWithTasks = new Set([...mainTasks, ...calendarTasks].map(data => data[0]))
        const emptyGoals = activeGoals.filter(goal => !goalIdsWithTasks.has(goal.id))

        openTasksArray.push([
            date,
            amountTasks,
            estimationTasks,
            mainTasks,
            mentionTasks,
            suggestedTasks,
            workflowTasks,
            observedTasks,
            streamAndUserTasks,
            activeGoals,
            calendarTasks,
            emptyGoals,
        ])
    }

    return openTasksArray
}

const processSubtaskChange = (
    subtasksByParentId,
    subtasksMap,
    areObservedTasks,
    areStreamAndUserTasks,
    showLaterTasks,
    showSomedayTasks,
    currentUserId,
    loggedUser,
    endOfDay,
    subtasksListToSortParentsId,
    changeType,
    subtask
) => {
    const { uid: loggedUserId } = loggedUser

    const { parentId, dueDateByObserversIds, isPublicFor } = subtask

    let needToProcessTheTask = subtask.inDone === false
    if (areObservedTasks) {
        const taskInBacklog = dueDateByObserversIds[currentUserId] === BACKLOG_DATE_NUMERIC
        const taskIsTodayOrOverdue = dueDateByObserversIds[currentUserId] <= endOfDay
        const taskIsLater = dueDateByObserversIds[currentUserId] > endOfDay && !taskInBacklog

        if (!Array.isArray(isPublicFor)) {
            console.warn('[OpenTasks] isPublicFor missing/invalid on observed task', {
                taskId: task && task.id,
                isPublicFor,
            })
        }
        const safeObservedIsPublicFor = Array.isArray(isPublicFor) ? isPublicFor : []
        const isPublicForLoggedUser =
            safeObservedIsPublicFor.includes(FEED_PUBLIC_FOR_ALL) ||
            (!loggedUser.isAnonymous && safeObservedIsPublicFor.includes(loggedUserId))

        const needToBeListedInThisDate = showSomedayTasks || taskIsTodayOrOverdue || (showLaterTasks && taskIsLater)
        needToProcessTheTask = subtask.inDone === false && isPublicForLoggedUser && needToBeListedInThisDate
    }

    const addSubtask = () => {
        if (needToProcessTheTask) {
            subtasksByParentId[parentId] = subtasksByParentId[parentId]
                ? subtasksByParentId[parentId].filter(taskItem => taskItem.id !== subtask.id)
                : []
            subtasksByParentId[parentId] = subtasksByParentId[parentId].concat(subtask)
            if (subtasksByParentId[parentId].length > 1) subtasksListToSortParentsId.add(parentId)
            areObservedTasks
                ? (subtasksMap.observedSubtasksById[subtask.id] = subtask)
                : areStreamAndUserTasks
                  ? (subtasksMap.streamAndUserSubtasksById[subtask.id] = subtask)
                  : (subtasksMap.userSubtasksById[subtask.id] = subtask)
        }
    }

    const deleteSubtask = parentId => {
        if (subtasksByParentId[parentId]) {
            subtasksByParentId[parentId] = subtasksByParentId[parentId].filter(taskItem => taskItem.id !== subtask.id)
            if (subtasksByParentId[parentId].length <= 1) {
                if (subtasksByParentId[parentId].length === 0) delete subtasksByParentId[parentId]
                subtasksListToSortParentsId.delete(parentId)
            }
        }
        areObservedTasks
            ? delete subtasksMap.observedSubtasksById[subtask.id]
            : areStreamAndUserTasks
              ? delete subtasksMap.streamAndUserSubtasksById[subtask.id]
              : delete subtasksMap.userSubtasksById[subtask.id]
    }

    if (changeType === 'added') {
        addSubtask()
    } else if (changeType === 'modified') {
        const oldSubtask = areObservedTasks
            ? subtasksMap.observedSubtasksById[subtask.id]
            : areStreamAndUserTasks
              ? subtasksMap.streamAndUserSubtasksById[subtask.id]
              : subtasksMap.userSubtasksById[subtask.id]

        const wasNeedToProcessTheTask = !!oldSubtask

        if (wasNeedToProcessTheTask) {
            const oldParentId = oldSubtask.parentId
            deleteSubtask(oldParentId)
        }
        addSubtask()
    } else {
        if (needToProcessTheTask) deleteSubtask(parentId)
    }
}

const getSubtaskInMap = (subtasksMap, areObservedTasks, areStreamAndUserTasks, taskId) => {
    const { observedSubtasksById, streamAndUserSubtasksById, userSubtasksById } = subtasksMap
    const subtask = areObservedTasks
        ? observedSubtasksById[taskId]
        : areStreamAndUserTasks
          ? streamAndUserSubtasksById[taskId]
          : userSubtasksById[taskId]
    return subtask
}

const processTaskChanges = (
    projectId,
    changes,
    loggedUser,
    currentUserId,
    endOfDay,
    storedTasks,
    estimationByDate,
    amountOfTasksByDate,
    tasksMap,
    areObservedTasks,
    areStreamAndUserTasks,
    dayDateFormated,
    showLaterTasks,
    showSomedayTasks,
    subtasksByParentId,
    subtasksMap,
    assistantProfileMode = false
) => {
    const listsToSort = {}
    const subtasksListToSortParentsId = new Set()

    for (let change of changes) {
        const changeType = change.type
        const task = mapTaskData(change.doc.id, change.doc.data())
        if (task.parentId) {
            if (changeType === 'modified') {
                const oldSubtask = getSubtaskInMap(subtasksMap, areObservedTasks, areStreamAndUserTasks, task.id)
                if (!oldSubtask) {
                    processTaskChange(
                        projectId,
                        loggedUser,
                        currentUserId,
                        endOfDay,
                        storedTasks,
                        estimationByDate,
                        amountOfTasksByDate,
                        tasksMap,
                        areObservedTasks,
                        areStreamAndUserTasks,
                        dayDateFormated,
                        showLaterTasks,
                        showSomedayTasks,
                        listsToSort,
                        'removed',
                        task,
                        assistantProfileMode
                    )
                }
            }

            processSubtaskChange(
                subtasksByParentId,
                subtasksMap,
                areObservedTasks,
                areStreamAndUserTasks,
                showLaterTasks,
                showSomedayTasks,
                currentUserId,
                loggedUser,
                endOfDay,
                subtasksListToSortParentsId,
                changeType,
                task
            )
        } else {
            if (changeType === 'modified') {
                const oldSubtask = getSubtaskInMap(subtasksMap, areObservedTasks, areStreamAndUserTasks, task.id)
                if (oldSubtask) {
                    processSubtaskChange(
                        subtasksByParentId,
                        subtasksMap,
                        areObservedTasks,
                        areStreamAndUserTasks,
                        showLaterTasks,
                        showSomedayTasks,
                        currentUserId,
                        loggedUser,
                        endOfDay,
                        subtasksListToSortParentsId,
                        'removed',
                        oldSubtask
                    )
                }
            }
            processTaskChange(
                projectId,
                loggedUser,
                currentUserId,
                endOfDay,
                storedTasks,
                estimationByDate,
                amountOfTasksByDate,
                tasksMap,
                areObservedTasks,
                areStreamAndUserTasks,
                dayDateFormated,
                showLaterTasks,
                showSomedayTasks,
                listsToSort,
                changeType,
                task,
                assistantProfileMode
            )
        }
    }

    sortTasksListThatHaveNewTasks(storedTasks, listsToSort)
    const openTasksArray = generateOpenTasksArray(storedTasks, dayDateFormated, amountOfTasksByDate, estimationByDate)

    for (let parentId of subtasksListToSortParentsId) {
        subtasksByParentId[parentId] = orderBy(subtasksByParentId[parentId], 'sortIndex', 'desc')
    }

    return { openTasksArray, subtasksByTasks: { ...subtasksByParentId } }
}

//COMMON UNWATCH FUNCTIONS

const unwatchSpecific = (projectId, currentUserId, assigneeUserId, watcher) => {
    if (watcher[projectId] && watcher[projectId][currentUserId] && watcher[projectId][currentUserId][assigneeUserId]) {
        watcher[projectId][currentUserId][assigneeUserId]()
        delete watcher[projectId][currentUserId][assigneeUserId]
    }
}

const unwatch = (projectId, currentUserId, watcher) => {
    if (watcher[projectId] && watcher[projectId][currentUserId]) {
        for (let unsubIndex in watcher[projectId][currentUserId]) {
            unwatchSpecific(projectId, currentUserId, unsubIndex, watcher)
        }
        if (size(watcher[projectId][currentUserId]) === 0) delete watcher[projectId][currentUserId]
        if (size(watcher[projectId]) === 0) delete watcher[projectId]
    }
}

// WORKSTREAMS MIXED BOARDS

const watchStreamAndUserOpenTasksInBatches = (
    projectId,
    callback,
    storedTasks,
    estimationByDate,
    amountOfTasksByDate,
    tasksMap,
    showLaterTasks,
    showSomedayTasks,
    subtasksByParentId,
    subtasksMap,
    trackConnectionHealth = true
) => {
    const { currentUser, loggedUser } = store.getState()
    const currentUserId = currentUser.uid
    const userIsStream = currentUserId.startsWith(WORKSTREAM_ID_PREFIX)

    let userIdList =
        currentUser.recorderUserId || userIsStream || !!currentUser.temperature
            ? []
            : [...(currentUser?.workstreams[projectId] || []), DEFAULT_WORKSTREAM_ID]

    for (let i = 0; i < userIdList.length; i++) {
        watchStreamAndUserOpenTasks(
            projectId,
            callback,
            storedTasks,
            estimationByDate,
            amountOfTasksByDate,
            tasksMap,
            showLaterTasks,
            showSomedayTasks,
            currentUser,
            loggedUser,
            userIdList[i],
            subtasksByParentId,
            subtasksMap,
            trackConnectionHealth
        )
    }
}

const getOpenStreamAndUserTasksQuery = (
    projectId,
    showLaterTasks,
    showSomedayTasks,
    endOfDay,
    loggedUser,
    assigneeUserId,
    endOfTomorrow
) => {
    const { uid: loggedUserId, isAnonymous } = loggedUser
    const allowUserIds = isAnonymous ? [FEED_PUBLIC_FOR_ALL] : [FEED_PUBLIC_FOR_ALL, loggedUserId]

    let query = getDb()
        .collection(`items/${projectId}/tasks`)
        .where('inDone', '==', false)
        .where('userId', '==', assigneeUserId)
        .where('readerIds', 'array-contains', allowUserIds[allowUserIds.length - 1])

    // Support three expand states (same as getOpenTasksQuery)
    if (!showLaterTasks && !showSomedayTasks) {
        // State 0: Only today
        query = query.where('dueDate', '<=', endOfDay)
    } else if (showLaterTasks && !showSomedayTasks) {
        // State 1: Today + tomorrow only
        if (endOfTomorrow) {
            query = query.where('dueDate', '<=', endOfTomorrow).where('dueDate', '<', BACKLOG_DATE_NUMERIC)
        } else {
            query = query.where('dueDate', '<', BACKLOG_DATE_NUMERIC)
        }
    }
    // State 2: No date filter

    return query
}

const watchStreamAndUserOpenTasks = (
    projectId,
    callback,
    storedTasks,
    estimationByDate,
    amountOfTasksByDate,
    tasksMap,
    showLaterTasks,
    showSomedayTasks,
    currentUser,
    loggedUser,
    assigneeUserId,
    subtasksByParentId,
    subtasksMap,
    trackConnectionHealth = true
) => {
    const { taskListWatchersVars } = store.getState()
    const currentUserId = currentUser.uid

    const date = moment()
    const endOfDay = date.endOf('day').valueOf()
    const endOfTomorrow = moment().endOf('day').add(1, 'day').valueOf()
    const dayDateFormated = TODAY_DATE

    let query = getOpenStreamAndUserTasksQuery(
        projectId,
        showLaterTasks,
        showSomedayTasks,
        endOfDay,
        loggedUser,
        assigneeUserId,
        endOfTomorrow
    )

    let cacheChanges = []
    let initialSnapshotDelivered = false
    const gate = createCachedSnapshotGate(() => handleStreamAndUserTasksSnapshot, { trackConnectionHealth })
    const snapshotPerformance = createFirstSnapshotPerformance(
        { object_type: 'tasks', scope: 'project', source: 'workstream_open_tasks' },
        { sampleRate: 0.02 }
    )
    function handleStreamAndUserTasksSnapshot(querySnapshot) {
        const liveChanges = querySnapshot.docChanges()
        const buffered = gate.shouldBuffer(querySnapshot)
        snapshotPerformance.observe(querySnapshot, buffered)
        if (buffered) {
            cacheChanges = [...cacheChanges, ...liveChanges]
        } else {
            // AT-2337: see the note in watchUserOpenTasks - coalesce this snapshot's
            // dispatches into one store notification.
            runInDispatchBatch(() => {
                const isInitialSnapshot = !initialSnapshotDelivered
                const knownItems = {
                    ...Object.fromEntries(
                        Object.entries(tasksMap.streamAndUserTasksById).filter(
                            ([, task]) => task.userId === assigneeUserId
                        )
                    ),
                    ...Object.fromEntries(
                        Object.entries(subtasksMap.streamAndUserSubtasksById).filter(
                            ([, task]) => task.userId === assigneeUserId
                        )
                    ),
                }
                const mergedChanges = isInitialSnapshot
                    ? reconcileInitialSnapshotChanges(querySnapshot.docs, knownItems)
                    : [...cacheChanges, ...liveChanges]
                if (isInitialSnapshot) initialSnapshotDelivered = true

                let subtasks = { ...subtasksByParentId }

                if (mergedChanges.length > 0) {
                    const { openTasksArray, subtasksByTasks } = processTaskChanges(
                        projectId,
                        mergedChanges,
                        loggedUser,
                        currentUserId,
                        endOfDay,
                        storedTasks,
                        estimationByDate,
                        amountOfTasksByDate,
                        tasksMap,
                        false,
                        true,
                        dayDateFormated,
                        showLaterTasks,
                        showSomedayTasks,
                        subtasksByParentId,
                        subtasksMap
                    )
                    subtasks = subtasksByTasks
                    callback(openTasksArray)
                    batchDispatch(
                        setOpenTasksMap(projectId, {
                            ...tasksMap.observedTasksById,
                            ...tasksMap.userTasksById,
                            ...tasksMap.streamAndUserTasksById,
                        })
                    )
                } else if (Object.keys(storedTasks).length === 0) {
                    callback([[dayDateFormated, 0, 0, [], [], [], [], [], [], [], [], []]])
                    batchDispatch(setOpenTasksMap(projectId, {}))
                }

                // Save vars at the end
                batchDispatch(
                    setTaskListWatchersVars({
                        ...taskListWatchersVars,
                        storedTasks,
                        estimationByDate,
                        amountOfTasksByDate,
                        tasksMap,
                        subtasksByParentId,
                        subtasksMap,
                    })
                )

                batchDispatch(
                    setOpenSubtasksMap(projectId, {
                        ...subtasksMap.observedSubtasksById,
                        ...subtasksMap.userSubtasksById,
                        ...subtasksMap.streamAndUserSubtasksById,
                    })
                )

                const instanceKey = projectId + currentUserId
                batchDispatch(updateSubtaskByTask(instanceKey, subtasks))

                cacheChanges = []
            })
        }
    }
    const unsub = gate.wrapUnsubscribe(
        query.onSnapshot({ includeMetadataChanges: true }, handleStreamAndUserTasksSnapshot)
    )

    setProperty(streamAndUserOpenTasks, [projectId, currentUserId, assigneeUserId], unsub)
}

export const removeWatchersForOneStreamAndUser = (projectId, currentUserId, assigneeUserId) => {
    unwatchSpecific(projectId, currentUserId, assigneeUserId, streamAndUserOpenTasks)
}

export const addWatchersForOneStreamAndUser = (
    projectId,
    callbackOpenTasks,
    storedTasks,
    estimationByDate,
    amountOfTasksByDate,
    tasksMap,
    subtasksByParentId,
    subtasksMap,
    showLaterTasks,
    showSomedayTasks,
    assigneeUserId,
    trackConnectionHealth = true
) => {
    const { currentUser, loggedUser } = store.getState()

    watchStreamAndUserOpenTasks(
        projectId,
        callbackOpenTasks,
        storedTasks,
        estimationByDate,
        amountOfTasksByDate,
        tasksMap,
        showLaterTasks,
        showSomedayTasks,
        currentUser,
        loggedUser,
        assigneeUserId,
        subtasksByParentId,
        subtasksMap,
        trackConnectionHealth
    )
}

////GOALS ////

function watchEmptyGoals(
    projectId,
    callback,
    storedTasks,
    goalsMap,
    showLaterTasks,
    showSomedayTasks,
    estimationByDate,
    amountOfTasksByDate,
    trackConnectionHealth = true
) {
    // AT-2337: this used to read `currentUserId` one line ABOVE its own `const`
    // declaration. Babel's block-scoping transform rewrites `const` to `var`, so
    // instead of the ReferenceError you would get under real ES modules it quietly
    // passed `undefined` and the unwatch matched nothing - `watcher[projectId][undefined]`
    // is always falsy. It is currently harmless only because `watchOpenTasks` already
    // unwatched this same watcher correctly a few lines earlier; hoist the read so the
    // guard actually does what it says.
    const { currentUser, loggedUser } = store.getState()
    const currentUserId = currentUser.uid
    const loggedUserId = loggedUser.uid
    const accessReaderId = loggedUser.isAnonymous ? FEED_PUBLIC_FOR_ALL : loggedUserId

    unwatchEmptyGoalsWatcher(projectId, currentUserId, activeMilestoneEmptyGoals)

    const dayDateFormated = TODAY_DATE

    const date = moment()
    const endOfDay = date.endOf('day').valueOf()
    const endOfTomorrow = moment().endOf('day').add(1, 'day').valueOf()

    const ownerId = getOwnerId(projectId, currentUserId)
    let cacheChanges = []
    let initialSnapshotDelivered = false

    let query = getDb()
        .collection(`goals/${projectId}/items`)
        .where(getRoleIdsVisibleToField(String(accessReaderId)), 'array-contains', currentUserId)

    const gate = createCachedSnapshotGate(() => handleEmptyGoalsSnapshot, { trackConnectionHealth })
    const snapshotPerformance = createFirstSnapshotPerformance(
        { object_type: 'goals', scope: 'project', source: 'empty_goals_in_tasks' },
        { sampleRate: 0.02 }
    )
    function handleEmptyGoalsSnapshot(querySnapshot) {
        const liveChanges = querySnapshot.docChanges()
        const buffered = gate.shouldBuffer(querySnapshot)
        snapshotPerformance.observe(querySnapshot, buffered)
        if (buffered) {
            cacheChanges = [...cacheChanges, ...liveChanges]
        } else {
            const isInitialSnapshot = !initialSnapshotDelivered
            const mergedChanges = isInitialSnapshot
                ? reconcileInitialSnapshotChanges(querySnapshot.docs, goalsMap)
                : [...cacheChanges, ...liveChanges]
            if (isInitialSnapshot) initialSnapshotDelivered = true
            if (mergedChanges.length > 0) {
                const openTasksArray = processEmptyGoalChanges(
                    mergedChanges,
                    currentUserId,
                    endOfDay,
                    endOfTomorrow,
                    storedTasks,
                    estimationByDate,
                    amountOfTasksByDate,
                    goalsMap,
                    dayDateFormated,
                    showLaterTasks,
                    showSomedayTasks,
                    ownerId
                )
                // Empty goals update the combined board, but they are not the
                // observed-task stream and must not complete its loading flag.
                callback(openTasksArray)

                querySnapshot.forEach(doc => {
                    const goal = mapGoalData(doc.id, doc.data())
                    const { progress, dynamicProgress } = goal
                    if (progress === DYNAMIC_PERCENT && dynamicProgress === 100) return
                })
            } else if (Object.keys(storedTasks).length === 0) {
                callback([[dayDateFormated, 0, 0, [], [], [], [], [], [], [], [], []]])
            }
            cacheChanges = []
        }
    }
    const unsub = gate.wrapUnsubscribe(query.onSnapshot({ includeMetadataChanges: true }, handleEmptyGoalsSnapshot))

    activeMilestoneEmptyGoals[projectId] = { [currentUserId]: unsub }
}

const processEmptyGoalChanges = (
    changes,
    currentUserId,
    endOfDay,
    endOfTomorrow,
    storedTasks,
    estimationByDate,
    amountOfTasksByDate,
    goalsMap,
    dayDateFormated,
    showLaterTasks,
    showSomedayTasks,
    expectedOwnerId
) => {
    const { uid: loggedUserId, isAnonymous } = store.getState().loggedUser

    for (let change of changes) {
        const goalId = change.id ? change.id : change.doc.id
        const type = change.type
        const goal = change.data ? change.data : mapGoalData(goalId, change.doc.data())
        const { assigneesReminderDate, isPublicFor, progress, dynamicProgress, ownerId } = goal
        const userReminderDate = assigneesReminderDate[currentUserId]
        const goalInBacklog = userReminderDate === BACKLOG_DATE_NUMERIC
        const goalIsTodayOrOverdue = userReminderDate <= endOfDay
        const goalIsTomorrow = endOfTomorrow && userReminderDate > endOfDay && userReminderDate <= endOfTomorrow
        const goalIsLater = userReminderDate > endOfDay && !goalInBacklog
        const date = goalIsTodayOrOverdue
            ? dayDateFormated
            : goalInBacklog
              ? BACKLOG_DATE_STRING
              : moment(userReminderDate).format('YYYYMMDD')

        if (!Array.isArray(isPublicFor)) {
            console.warn('[OpenTasks] isPublicFor missing/invalid on goal', {
                goalId,
                isPublicFor,
            })
        }
        const safeGoalIsPublicFor = Array.isArray(isPublicFor) ? isPublicFor : []

        const isPublic =
            safeGoalIsPublicFor.includes(FEED_PUBLIC_FOR_ALL) ||
            (!isAnonymous && safeGoalIsPublicFor.includes(loggedUserId))
        const isNotCompleted = progress !== 100 && (progress !== DYNAMIC_PERCENT || dynamicProgress !== 100)

        // Support three expand states:
        // State 0 (both false): only today/overdue
        // State 1 (showLaterTasks=true, showSomedayTasks=false): today + tomorrow only
        // State 2 (both true): all including someday
        const needToBeListedInThisDate =
            goalIsTodayOrOverdue ||
            (showLaterTasks && !showSomedayTasks && goalIsTomorrow) ||
            (showLaterTasks && showSomedayTasks && goalIsLater) ||
            (showSomedayTasks && goalInBacklog)
        let needToProcessTheGoal = isPublic && ownerId === expectedOwnerId && isNotCompleted && needToBeListedInThisDate
        const addTask = () => {
            if (needToProcessTheGoal) {
                if (!storedTasks[date]) {
                    storedTasks[date] = {}
                    estimationByDate[date] = ESTIMATION_0_MIN
                    amountOfTasksByDate[date] = 0
                }

                if (!storedTasks[date][ACTIVE_GOALS_INDEX]) storedTasks[date][ACTIVE_GOALS_INDEX] = []
                storedTasks[date][ACTIVE_GOALS_INDEX].push(goal)
                goalsMap[goalId] = goal
            }
        }

        const deleteTask = date => {
            delete goalsMap[goalId]

            storedTasks[date][ACTIVE_GOALS_INDEX] = storedTasks[date][ACTIVE_GOALS_INDEX].filter(
                goalItem => goalItem.id !== goalId
            )

            if (Object.keys(storedTasks[date][ACTIVE_GOALS_INDEX]).length === 0) {
                delete storedTasks[date][ACTIVE_GOALS_INDEX]
                const amountTypesInDate = Object.keys(storedTasks[date]).length
                if (amountTypesInDate === 0) {
                    delete storedTasks[date]
                    delete estimationByDate[date]
                    delete amountOfTasksByDate[date]
                }
            }
        }

        //PROCESS THE CHANGE
        if (type === 'modified') {
            const oldGoal = goalsMap[goalId]

            const wasNeedToProcessTheTask = !!oldGoal
            if (wasNeedToProcessTheTask) {
                const oldUserReminderDate = oldGoal.assigneesReminderDate[currentUserId]

                const oldGoalInBacklog = oldUserReminderDate === BACKLOG_DATE_NUMERIC
                const oldGoalIsTodayOrOverdue = oldUserReminderDate <= endOfDay && !oldGoalInBacklog

                const oldDate = oldGoalIsTodayOrOverdue
                    ? dayDateFormated
                    : oldGoalInBacklog
                      ? BACKLOG_DATE_STRING
                      : moment(oldUserReminderDate).format('YYYYMMDD')

                deleteTask(oldDate)
            }
            addTask()
        } else if (type === 'added') {
            const notExistGoal = !goalsMap[goalId]
            if (notExistGoal) addTask()
        } else {
            if (needToProcessTheGoal) deleteTask(date)
        }
    }

    //CONVERT OBJECT IN ARRAYS
    const openTasksArray = generateOpenTasksArray(storedTasks, dayDateFormated, amountOfTasksByDate, estimationByDate)

    return openTasksArray
}

const unwatchEmptyGoalsWatcher = (projectId, currentUserId, watcher) => {
    if (watcher[projectId] && watcher[projectId][currentUserId]) {
        watcher[projectId][currentUserId]()
        delete watcher[projectId][currentUserId]
        if (Object.keys(watcher[projectId]).length === 0) delete watcher[projectId]
    }
}

export const contractOpenTasks = (projectId, instanceKey, openTasks, updateTaks) => {
    store.dispatch([setLaterTasksExpanded(false), setSomedayTasksExpanded(false)])
    // First, unwatch existing listeners to prevent stale data from being pushed
    // This stops all Firestore listeners for this project
    unwatchOpenTasks(projectId, instanceKey)

    // Set up new watchers that only fetch today's tasks
    // Use keepMainDayData=false since we just cleared the data with unwatchOpenTasks
    // These watchers will update the UI when Firestore data arrives
    watchOpenTasks(projectId, updateTaks, false, false, false, instanceKey)
}

export const contractSomedayOpenTasks = (projectId, instanceKey, openTasks, updateTaks) => {
    const allOpenTasks = [...openTasks]
    allOpenTasks.pop()
    updateTaks(allOpenTasks, false)
    watchOpenTasks(projectId, updateTaks, true, false, true, instanceKey)
    store.dispatch(setSomedayTasksExpanded(false))
}

export const filterOpTasks = (instanceKey, tasks, projectId) => {
    const {
        hashtagFilters,
        taskPriorityFilters,
        taskVmStateFilters = [],
        taskVmStatesByTask = {},
        subtaskByTaskStore,
    } = store.getState()
    const filtersArray = Array.from(hashtagFilters.keys())
    let filteredOpenTasks = filtersArray.length > 0 ? filterOpenTasks(tasks) : tasks
    if (taskPriorityFilters.length > 0) {
        filteredOpenTasks = filterOpenTasksSectionsByPriority(
            filteredOpenTasks,
            taskPriorityFilters,
            subtaskByTaskStore[instanceKey]
        )
    }
    if (taskVmStateFilters.length > 0) {
        filteredOpenTasks = filterOpenTasksSectionsByVmState(
            filteredOpenTasks,
            taskVmStateFilters,
            taskVmStatesByTask,
            subtaskByTaskStore[instanceKey],
            projectId
        )
    }
    batchDispatch(updateFilteredOpenTasks(instanceKey, filteredOpenTasks))
}

export const updateOpTasks = (
    projectId,
    instanceKey,
    initialTasks,
    initialLoadingInOpenTasks,
    setProjectsHaveTasksInFirstDay,
    inSelectedProject
) => {
    // AT-2337: this whole body used to emit 5-7 individual store notifications and
    // it runs once per project per snapshot. Batch it. When it is reached from a
    // snapshot handler the batch is already open and this simply joins it, so the
    // full snapshot still costs exactly one notification.
    const openTasks = runInDispatchBatch(() => {
        const openTasks = inSelectedProject ? initialTasks : taskToShowInAllProjects(instanceKey, initialTasks)

        // Check if there are any visible tasks (main, email, calendar) or goals for the first day
        const thereAreNotTasksInFirstDay =
            openTasks.length === 0 ||
            (openTasks[0][AMOUNT_TASKS_INDEX] === 0 && openTasks[0][ACTIVE_GOALS_INDEX].length === 0)

        const todayEmptyGoalsAmount =
            openTasks.length === 0 || openTasks[0][ACTIVE_GOALS_INDEX].length === 0
                ? 0
                : openTasks[0][ACTIVE_GOALS_INDEX].length
        batchDispatch(setTodayEmptyGoalsTotalAmountInOpenTasksView(projectId, todayEmptyGoalsAmount))

        batchDispatch(updateThereAreNotTasksInFirstDay(instanceKey, thereAreNotTasksInFirstDay))

        updateAndFilterTasksTasks(instanceKey, openTasks, projectId)

        // Expanding later/someday tasks keeps the rows already on screen and adds
        // one task-shaped ghost until the replacement watcher resolves. Clear that
        // incremental state only after the refreshed list has reached Redux.
        // NOTE: read from the live store, not from a value written inside this
        // batch - nothing buffered here touches `taskListSingleLoading`.
        if (store.getState().taskListSingleLoading?.[instanceKey]) {
            batchDispatch(setTaskListSingleLoading(instanceKey, false))
        }

        // The two Firestore streams can finish on different renders. Publish the
        // corresponding loaded flag only after the merged, filtered list is in the
        // store so loading placeholders cannot disappear against stale task data.
        if (initialLoadingInOpenTasks === true) {
            batchDispatch(updateInitialLoadingEndOpenTasks(instanceKey, true))
        } else if (initialLoadingInOpenTasks === false) {
            batchDispatch(updateInitialLoadingEndObservedTasks(instanceKey, true))
        }

        return openTasks
    })

    // Persist only after the Redux batch has flushed. This records the render-ready projection,
    // not Firestore's mutable listener internals, and debounces the many per-project callbacks into
    // one idle IndexedDB write.
    scheduleTaskColdStartCachePersist(store.getState)

    if (setProjectsHaveTasksInFirstDay)
        setProjectsHaveTasksInFirstDay(projectsHaveTasksInFirstDay => {
            // Use AMOUNT_TASKS_INDEX which now includes calendar tasks for project amount calculation
            const projectAmount = openTasks[0]
                ? openTasks[0][AMOUNT_TASKS_INDEX] + openTasks[0][ACTIVE_GOALS_INDEX].length
                : 0
            if (!isEqual(projectsHaveTasksInFirstDay[projectId], projectAmount)) {
                return {
                    ...projectsHaveTasksInFirstDay,
                    [projectId]: projectAmount,
                }
            }
            return projectsHaveTasksInFirstDay
        })
}

export const taskToShowInAllProjects = (instanceKey, filteredOpenTasks) => {
    let hiddenTaskTypesExist = false

    // Determine if any tasks of the specifically "hidden" types exist
    for (const tasksByDate of filteredOpenTasks) {
        const typesToSumForHiddenCheck = [
            tasksByDate[MENTION_TASK_INDEX],
            tasksByDate[OBSERVED_TASKS_INDEX],
            tasksByDate[STREAM_AND_USER_TASKS_INDEX],
        ]

        for (const taskTypeGroupArray of typesToSumForHiddenCheck) {
            if (taskTypeGroupArray && taskTypeGroupArray.length > 0) {
                // taskTypeGroupArray is like [[groupId, goalsArray], [groupId, goalsArray]]
                for (const group of taskTypeGroupArray) {
                    // group is like [groupId, goalsArray]
                    const goalsArray = group[1]
                    if (goalsArray && goalsArray.length > 0) {
                        // goalsArray is like [[goalId, tasksArray], [goalId, tasksArray]]
                        for (const goalEntry of goalsArray) {
                            // goalEntry is like [goalId, tasksArray]
                            const tasksArray = goalEntry[1]
                            if (tasksArray && tasksArray.length > 0) {
                                hiddenTaskTypesExist = true
                                break // Found tasks in a hidden type
                            }
                        }
                    }
                    if (hiddenTaskTypesExist) break
                }
            }
            if (hiddenTaskTypesExist) break
        }
        if (hiddenTaskTypesExist) break // Found in this date, no need to check other dates
    }

    batchDispatch(updateThereAreHiddenNotMainTasks(instanceKey, hiddenTaskTypesExist))

    // Build taskToShow for display
    let taskToShow = []
    filteredOpenTasks.forEach(originalTasksByDate => {
        let currentMainTasksCount = 0
        if (originalTasksByDate[MAIN_TASK_INDEX]) {
            originalTasksByDate[MAIN_TASK_INDEX].forEach(tasksByGoal => {
                currentMainTasksCount += tasksByGoal[1].length
            })
        }

        let currentSuggestedTasksCount = 0
        if (originalTasksByDate[SUGGESTED_TASK_INDEX]) {
            originalTasksByDate[SUGGESTED_TASK_INDEX].forEach(tasksBySuggester => {
                tasksBySuggester[1].forEach(tasksByGoal => {
                    currentSuggestedTasksCount += tasksByGoal[1].length
                })
            })
        }

        // Calculate workflow tasks count
        let currentWorkflowTasksCount = 0
        if (originalTasksByDate[WORKFLOW_TASK_INDEX]) {
            originalTasksByDate[WORKFLOW_TASK_INDEX].forEach(tasksByGoal => {
                if (tasksByGoal && tasksByGoal[1] && Array.isArray(tasksByGoal[1])) {
                    currentWorkflowTasksCount += tasksByGoal[1].length
                }
            })
        }

        // Count empty goals (goals with reminder dates but no linked tasks)
        const currentEmptyGoalsCount = originalTasksByDate[EMPTY_SECTION_INDEX]
            ? originalTasksByDate[EMPTY_SECTION_INDEX].length
            : 0

        // AT-2377: calendar tasks live in their own bucket again, so they need their own count -
        // a day holding nothing but meetings must still show up in All projects.
        let currentCalendarTasksCount = 0
        if (originalTasksByDate[CALENDAR_TASK_INDEX]) {
            originalTasksByDate[CALENDAR_TASK_INDEX].forEach(tasksByGoal => {
                if (tasksByGoal && Array.isArray(tasksByGoal[1])) {
                    currentCalendarTasksCount += tasksByGoal[1].length
                }
            })
        }

        // Check if this date should be included in the output
        const shouldInclude =
            currentMainTasksCount > 0 ||
            currentSuggestedTasksCount > 0 ||
            currentCalendarTasksCount > 0 ||
            currentWorkflowTasksCount > 0 ||
            currentEmptyGoalsCount > 0

        // Include days with any task type that is visible in All Projects.
        if (shouldInclude) {
            const newTasksByDateEntry = [...originalTasksByDate] // Start with a shallow copy

            // Count every visible task type so projects containing only one of these sections remain visible.
            // Inbox-summary email tasks are part of MAIN_TASK_INDEX and need no separate count.
            newTasksByDateEntry[AMOUNT_TASKS_INDEX] =
                currentMainTasksCount +
                currentSuggestedTasksCount +
                currentCalendarTasksCount +
                currentWorkflowTasksCount

            // AT-2377: the show-more arrow must not appear on a day that hides nothing but meetings,
            // which always render in full inside the Calendar section.
            newTasksByDateEntry.hasCalendarTasks = currentCalendarTasksCount > 0
            newTasksByDateEntry.nonCalendarTasksCount =
                currentMainTasksCount + currentSuggestedTasksCount + currentWorkflowTasksCount

            // Preserve task types that should be VISIBLE in "All Projects" view
            newTasksByDateEntry[MAIN_TASK_INDEX] = originalTasksByDate[MAIN_TASK_INDEX] || []
            newTasksByDateEntry[CALENDAR_TASK_INDEX] = originalTasksByDate[CALENDAR_TASK_INDEX] || []
            newTasksByDateEntry[SUGGESTED_TASK_INDEX] = originalTasksByDate[SUGGESTED_TASK_INDEX] || [] // Display suggested tasks
            newTasksByDateEntry[WORKFLOW_TASK_INDEX] = originalTasksByDate[WORKFLOW_TASK_INDEX] || [] // Display workflow tasks

            // Preserve Active Goals and Empty Goals as they are handled by projectAmount or UI separately
            newTasksByDateEntry[ACTIVE_GOALS_INDEX] = originalTasksByDate[ACTIVE_GOALS_INDEX] || []
            newTasksByDateEntry[EMPTY_SECTION_INDEX] = originalTasksByDate[EMPTY_SECTION_INDEX] || []

            // Clear task types that are HIDDEN in "All Projects" summary view
            newTasksByDateEntry[MENTION_TASK_INDEX] = []
            newTasksByDateEntry[OBSERVED_TASKS_INDEX] = []
            newTasksByDateEntry[STREAM_AND_USER_TASKS_INDEX] = []

            // ESTIMATION_TASKS_INDEX is kept from original.
            newTasksByDateEntry[ESTIMATION_TASKS_INDEX] = originalTasksByDate[ESTIMATION_TASKS_INDEX]

            taskToShow.push(newTasksByDateEntry)
        }
    })

    return taskToShow
}

export const updateAndFilterTasksTasks = (instanceKey, tasks, projectId) => {
    batchDispatch(updateOpenTasks(instanceKey, tasks))
    filterOpTasks(instanceKey, tasks, projectId)
}

export function watchAllMilestones(projectId, watcherKey) {
    const { currentUser } = store.getState()
    const ownerId = getOwnerId(projectId, currentUser.uid)

    globalWatcherUnsub[watcherKey] = getDb()
        .collection(`goalsMilestones/${projectId}/milestonesItems`)
        .where('ownerId', '==', ownerId)
        .orderBy('date', 'asc')
        .onSnapshot(milestonesData => {
            const milestones = []
            const openMilestones = []
            let doneMilestone = []
            milestonesData.forEach(doc => {
                const milestone = mapMilestoneData(doc.id, doc.data())
                milestones.push(milestone)
                milestone.done ? doneMilestone.push(milestone) : openMilestones.push(milestone)
            })

            doneMilestone = sortBy(doneMilestone, [item => item.doneDate])
            doneMilestone.reverse()

            store.dispatch([
                setOpenMilestonesInProjectInTasks(projectId, openMilestones),
                setDoneMilestonesInProjectInTasks(projectId, doneMilestone),
            ])
            scheduleTaskColdStartCachePersist(store.getState)
        })
}

export function watchAllGoals(projectId, watcherKey) {
    const { currentUser, loggedUser } = store.getState()
    const { uid: loggedUserId, isAnonymous } = loggedUser
    const currentUserId = currentUser.uid

    const ownerId = getOwnerId(projectId, currentUserId)
    const allowUserIds = isAnonymous ? [FEED_PUBLIC_FOR_ALL] : [FEED_PUBLIC_FOR_ALL, loggedUserId]

    const query = getDb()
        .collection(`goals/${projectId}/items`)
        .where('readerIds', 'array-contains', allowUserIds[allowUserIds.length - 1])
        .where('ownerId', '==', ownerId)

    globalWatcherUnsub[watcherKey] = query.onSnapshot(goalsData => {
        const goals = []
        goalsData.forEach(doc => {
            const goal = mapGoalData(doc.id, doc.data())
            goals.push(goal)
        })
        const goalsById = {}
        goals.forEach(goal => {
            goalsById[goal.id] = goal
        })
        store.dispatch(setGoalsInProjectInTasks(projectId, goalsById))
        scheduleTaskColdStartCachePersist(store.getState)
    })
}

export function sortGoalTasksGorups(projectId, openMilestones, doneMilestones, goalsById, assigneeId, tasksGroups) {
    if (openMilestones && doneMilestones && goalsById) {
        const milestones = [
            ...openMilestones,
            { id: `${BACKLOG_MILESTONE_ID}${projectId}`, date: BACKLOG_DATE_NUMERIC, done: false },
            ...doneMilestones,
        ]

        const checkedGoalsById = {}
        let sortedGoals = []

        milestones.forEach(milestone => {
            const { date: milestoneDate, id: milestoneId, done } = milestone

            const milestoneGoals = []

            tasksGroups.forEach(goalTasksData => {
                const goalId = goalTasksData[0]
                const goal = goalsById[goalId]
                if (goal) {
                    const {
                        startingMilestoneDate,
                        completionMilestoneDate,
                        parentDoneMilestoneIds,
                        progress,
                        dynamicProgress,
                    } = goal
                    if (
                        !checkedGoalsById[goalId] &&
                        ((done && parentDoneMilestoneIds.includes(milestoneId)) ||
                            (startingMilestoneDate <= milestoneDate &&
                                completionMilestoneDate >= milestoneDate &&
                                (milestoneDate !== BACKLOG_DATE_NUMERIC ||
                                    (progress !== 100 && (progress !== DYNAMIC_PERCENT || dynamicProgress !== 100)))))
                    ) {
                        milestoneGoals.push(goal)
                        checkedGoalsById[goalId] = goal
                    }
                }
            })

            milestoneGoals.sort(
                (a, b) =>
                    (b.sortIndexByMilestone[milestoneId]
                        ? b.sortIndexByMilestone[milestoneId]
                        : Number.MAX_SAFE_INTEGER) -
                    (a.sortIndexByMilestone[milestoneId]
                        ? a.sortIndexByMilestone[milestoneId]
                        : Number.MAX_SAFE_INTEGER)
            )

            sortedGoals = [...sortedGoals, ...milestoneGoals]
        })

        const assigneeGoals = sortedGoals.filter(goal => goal.assigneesIds.includes(assigneeId))
        const otherGoals = sortedGoals.filter(goal => !goal.assigneesIds.includes(assigneeId))
        sortedGoals = [...assigneeGoals, ...otherGoals]

        const goalsPositionId = {}
        goalsPositionId[NOT_PARENT_GOAL_INDEX] = sortedGoals.length
        sortedGoals.forEach((goal, index) => {
            goalsPositionId[goal.id] = index
        })

        return goalsPositionId
    }
}
